<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Asistencia</span>
          <h2 class="workspace-title">Lista de asistentes y tickets emitidos</h2>
          <p class="workspace-copy">
            {{ showSensitiveData
              ? 'Revisa asistentes, estados de validación y datos de contacto para soporte operativo.'
              : 'Revisa asistentes, estados de validación y descargas sin exponer datos sensibles del comprador.' }}
          </p>
        </div>

        <div class="workspace-actions workspace-actions--compact assistance-header-actions">
          <button v-if="canViewInternalFinance" class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="hostViewPreview = !hostViewPreview">
            {{ hostViewPreview ? 'Ver como admin' : 'Ver como host' }}
          </button>

          <download-excel
            class="workspace-button workspace-button--small workspace-button--primary"
            :data="exportRows"
            :fields="exportFields"
            worksheet="Asistentes"
            name="Asistentes.xls"
          >
            <i class="fas fa-download"></i>
            <span>Descargar</span>
          </download-excel>
        </div>
      </div>

      <div class="workspace-metrics assistance-metrics">
        <article class="workspace-metric">
          <small>Total</small>
          <strong>{{ filteredEvents.length }}</strong>
          <p>Resultados en la vista actual.</p>
        </article>
        <article class="workspace-metric">
          <small>Aprobados</small>
          <strong>{{ statusCounts.approved }}</strong>
          <p>Pagos aprobados y ticket listo para operar.</p>
        </article>
        <article class="workspace-metric">
          <small>Pendientes</small>
          <strong>{{ statusCounts.pending }}</strong>
          <p>Compras aún sin aprobar.</p>
        </article>
        <article class="workspace-metric">
          <small>Revisión manual</small>
          <strong>{{ statusCounts.manual_review }}</strong>
          <p>Casos que requieren validación manual.</p>
        </article>
        <article class="workspace-metric">
          <small>Duplicados / rechazados</small>
          <strong>{{ statusCounts.duplicate + statusCounts.rejected }}</strong>
          <p>Pagos observados o rechazados.</p>
        </article>
      </div>

      <div class="workspace-toolbar assistance-toolbar">
        <form class="assistance-search" @submit.prevent="searchEvents">
          <label class="workspace-search assistance-search__field">
            <i class="fas fa-search"></i>
            <input v-model.trim="search" :placeholder="searchPlaceholder" type="text" />
          </label>

          <div class="workspace-actions workspace-actions--compact">
            <button class="workspace-button workspace-button--primary workspace-button--small" type="submit">Buscar</button>
            <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="resetSearch">Limpiar</button>
          </div>
        </form>

        <div class="workspace-segmented-control assistance-status-filter">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            :class="['workspace-segmented-control__item', { 'is-active': statusFilter === option.value }]"
            type="button"
            @click="statusFilter = option.value"
          >
            <span>{{ option.label }}</span>
          </button>
        </div>
      </div>

      <div v-if="!filteredEvents.length" class="workspace-empty">
        No hay asistentes para ese filtro.
      </div>

      <div v-else class="workspace-table-shell assistance-table-shell">
        <table class="workspace-table assistance-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>ID</th>
              <th>Nombre</th>
              <th>Apellido</th>
              <th>Localidad</th>
              <th>Código</th>
              <th>Monto</th>
              <th>Estado</th>
              <th>Tipo</th>
              <th>Comentario</th>
              <th v-if="showSensitiveData">Teléfono</th>
              <th v-if="showSensitiveData">Email</th>
              <th>Asistente</th>
              <th v-for="customField in activeCustomColumns" :key="customField.slug">{{ customField.label }}</th>
              <th v-if="showSeats">Asiento</th>
              <th v-if="showSeats">Sección</th>
              <th>Descarga</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(eventTicket, index) in filteredEvents" :key="eventTicket.id || `${eventTicket.invoice_number}-${index}`">
              <td data-label="Fecha">{{ paymentDateLabel(eventTicket) }}</td>
              <td data-label="ID">{{ paymentIdLabel(eventTicket) }}</td>
              <td data-label="Nombre">{{ eventTicket.first_name || '-' }}</td>
              <td data-label="Apellido">{{ eventTicket.last_name || '-' }}</td>
              <td data-label="Localidad">{{ eventTicket.product_name || '-' }}</td>
              <td data-label="Codigo">{{ eventTicket.product_code || '-' }}</td>
              <td data-label="Monto">${{ formatMoney(eventTicket.amount) }}</td>
              <td data-label="Estado">
                <span :class="['workspace-status-pill', statusPillClass(eventTicket)]">{{ statusLabel(eventTicket) }}</span>
              </td>
              <td data-label="Tipo">{{ paymentTypeLabel(eventTicket.payment && eventTicket.payment.type) }}</td>
              <td data-label="Comentario">{{ paymentCommentLabel(eventTicket) }}</td>
              <td v-if="showSensitiveData" data-label="Telefono">{{ eventTicket.phone || '-' }}</td>
              <td v-if="showSensitiveData" data-label="Email">{{ eventTicket.user_email || '-' }}</td>
              <td data-label="Asistente">{{ eventTicket.usage || 0 }}</td>
              <td v-for="customField in activeCustomColumns" :key="`${eventTicket.id || eventTicket.invoice_number}-${customField.slug}`" :data-label="customField.label">{{ customFieldValue(eventTicket, customField.slug) || '-' }}</td>
              <td v-if="showSeats" data-label="Asiento">{{ eventTicket.seat_name || '-' }}</td>
              <td v-if="showSeats" data-label="Seccion">{{ eventTicket.venue_section || '-' }}</td>
              <td data-label="Descarga">
                <a v-if="eventTicket.is_automatic && event.tickets_type === 1 && eventTicket.payment && eventTicket.payment.file" class="workspace-inline-link" :href="eventTicket.payment.file" target="_blank">Descargar</a>
                <button
                  v-else-if="(!eventTicket.is_automatic || event.tickets_type === 2) && !is_generating"
                  class="workspace-chip-button"
                  type="button"
                  @click="generatePdf(index)"
                >
                  Descargar
                </button>
                <span v-else>Cargando...</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="workspace-actions workspace-actions--end">
        <spring-spinner v-if="loading" v-show="next" :size="48" color="#8cf3e8" />
        <button v-show="next && !loading" class="workspace-button" type="button" @click="getEvents">Cargar más</button>
      </div>
    </article>

    <PdfGenerationManual v-if="is_generating && currentTicket" :event="event" :ticket="currentTicket" :invoice="currentTicket.invoice_number" @messageFromPdfGeneration="afterPdf" />
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { SpringSpinner } from 'epic-spinners'
import PdfGenerationManual from '@/components/sales/PdfGenerationManual.vue'
import JsonExcel from 'vue-json-excel3'

export default {
  name: 'AssistanceList',

  components: {
    SpringSpinner,
    PdfGenerationManual,
    downloadExcel: JsonExcel,
  },

  props: {
    event: {
      required: true,
    },
  },

  data() {
    return {
      events: [],
      next: null,
      search: '',
      loading: false,
      isStaff: localStorage.getItem('is_staff'),
      currentTicket: null,
      is_generating: false,
      statusFilter: 'all',
      hostViewPreview: false,
    }
  },

  computed: {
    canViewInternalFinance() {
      return Boolean(this.event && this.event.can_view_internal_finance)
    },

    showSensitiveData() {
      return this.canViewInternalFinance && !this.hostViewPreview
    },

    showSeats() {
      return this.isStaff === true || this.isStaff === 'true'
    },

    searchPlaceholder() {
      return this.showSensitiveData
        ? 'Buscar por nombre, apellido, correo, código o ID'
        : 'Buscar por nombre, apellido, código o ID'
    },

    statusOptions() {
      return [
        { value: 'all', label: 'Todos' },
        { value: 'approved', label: 'Aprobado' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'duplicate', label: 'Duplicado' },
        { value: 'rejected', label: 'Rechazado' },
        { value: 'manual_review', label: 'Req. revisión manual' },
      ]
    },

    filteredEvents() {
      const term = this.search.trim().toLowerCase()

      return this.events.filter((eventTicket) => {
        const statusKey = this.statusKey(eventTicket)
        if (this.statusFilter !== 'all' && statusKey !== this.statusFilter) {
          return false
        }

        if (!term) {
          return true
        }

        const haystack = [
          eventTicket.first_name,
          eventTicket.last_name,
          eventTicket.user_email,
          eventTicket.phone,
          eventTicket.product_code,
          eventTicket.product_name,
          eventTicket.invoice_number,
          this.paymentIdLabel(eventTicket),
        ].join(' ').toLowerCase()

        return haystack.includes(term)
      })
    },

    statusCounts() {
      return this.events.reduce((accumulator, eventTicket) => {
        const key = this.statusKey(eventTicket)
        if (!accumulator[key]) {
          accumulator[key] = 0
        }
        accumulator[key] += 1
        return accumulator
      }, {
        approved: 0,
        pending: 0,
        duplicate: 0,
        rejected: 0,
        manual_review: 0,
        other: 0,
      })
    },

    activeCustomColumns() {
      const catalog = Array.isArray(this.event && this.event.guest_field_catalog) ? this.event.guest_field_catalog : []
      return catalog.filter((field) => field && field.is_active !== false && Number(field.assigned_tickets_count || 0) > 0)
    },

    exportFields() {
      const fields = {
        Fecha: 'date',
        ID: 'payment_id',
        Nombre: 'first_name',
        Apellido: 'last_name',
        Localidad: 'product_name',
        Código: 'product_code',
        Monto: 'amount',
        Estado: 'status',
        Tipo: 'type',
        Comentario: 'comment',
        Asistente: 'usage',
      }

      if (this.showSensitiveData) {
        fields.Teléfono = 'phone'
        fields.Email = 'email'
      }

      this.activeCustomColumns.forEach((field) => {
        fields[field.label] = `custom_field_map.${field.slug}`
      })

      return fields
    },

    exportRows() {
      return this.filteredEvents.map((eventTicket) => ({
        date: this.paymentDateLabel(eventTicket),
        payment_id: this.paymentIdLabel(eventTicket),
        first_name: eventTicket.first_name || '',
        last_name: eventTicket.last_name || '',
        product_name: eventTicket.product_name || '',
        product_code: eventTicket.product_code || '',
        amount: this.formatMoney(eventTicket.amount),
        status: this.statusLabel(eventTicket),
        type: this.paymentTypeLabel(eventTicket.payment && eventTicket.payment.type),
        comment: this.paymentCommentLabel(eventTicket),
        phone: this.showSensitiveData ? (eventTicket.phone || '') : '',
        email: this.showSensitiveData ? (eventTicket.user_email || '') : '',
        usage: eventTicket.usage || 0,
        custom_field_map: this.activeCustomColumns.reduce((accumulator, field) => ({
          ...accumulator,
          [field.slug]: this.customFieldValue(eventTicket, field.slug),
        }), {}),
      }))
    },
  },

  created() {
    this.getEvents()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.events = []
        this.next = null
        this.hostViewPreview = false
        this.getEvents()
      },
    },
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) {
        return 'n/a'
      }

      return new Intl.DateTimeFormat('default', { dateStyle: 'short' }).format(new Date(dateString))
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    paymentDateLabel(eventTicket) {
      return eventTicket && eventTicket.payment && eventTicket.payment.id !== 'n/a'
        ? this.formatDate(eventTicket.payment.date)
        : 'n/a'
    },

    paymentIdLabel(eventTicket) {
      return eventTicket && eventTicket.payment && eventTicket.payment.id ? eventTicket.payment.id : 'n/a'
    },

    paymentTypeLabel(type) {
      if (type === 1) return 'Efectivo'
      if (type === 2) return 'Transferencia'
      if (type === 3) return 'Tarjeta'
      if (type === 5) return 'Tarjeta (POS)'
      return 'n/a'
    },

    paymentCommentLabel(eventTicket) {
      const comment = eventTicket && eventTicket.payment ? eventTicket.payment.comment : ''
      if (!comment || comment === 'fomoaut') {
        return 'n/a'
      }
      return comment
    },

    customFieldValue(eventTicket, slug) {
      const fieldMap = eventTicket && eventTicket.custom_field_map ? eventTicket.custom_field_map : {}
      return fieldMap && fieldMap[slug] ? fieldMap[slug] : ''
    },

    isDuplicate(eventTicket) {
      const comment = (eventTicket && eventTicket.payment && eventTicket.payment.comment) ? String(eventTicket.payment.comment).toLowerCase() : ''
      return comment.includes('duplicado')
    },

    statusKey(eventTicket) {
      const paymentStatus = Number(eventTicket && eventTicket.payment ? eventTicket.payment.status : null)
      if (this.isDuplicate(eventTicket)) return 'duplicate'
      if (paymentStatus === 1) return 'approved'
      if (paymentStatus === 0) return 'pending'
      if (paymentStatus === 3) return 'manual_review'
      if (paymentStatus === 4) return 'rejected'
      return 'other'
    },

    statusLabel(eventTicket) {
      const key = this.statusKey(eventTicket)
      if (key === 'approved') return 'Aprobado'
      if (key === 'pending') return 'Pendiente'
      if (key === 'duplicate') return 'Duplicado'
      if (key === 'rejected') return 'Rechazado'
      if (key === 'manual_review') return 'Req. revisión manual'
      return 'Sin clasificar'
    },

    statusPillClass(eventTicket) {
      const key = this.statusKey(eventTicket)
      if (key === 'approved') return 'is-active'
      if (key === 'pending' || key === 'manual_review') return 'is-warning'
      if (key === 'duplicate' || key === 'rejected') return 'is-off'
      return ''
    },

    resetSearch() {
      this.search = ''
      this.events = []
      this.next = null
      this.getEvents()
    },

    searchEvents() {
      this.events = []
      this.next = null
      this.getEvents()
    },

    generatePdf(index) {
      this.currentTicket = this.filteredEvents[index] || null
      this.is_generating = Boolean(this.currentTicket)
    },

    afterPdf() {
      this.currentTicket = null
      this.is_generating = false
    },

    getEvents() {
      let endpoint = `/api/invoice-items/?event=${this.event.id}`

      if (this.search) {
        endpoint = `/api/invoice-items/?search=${encodeURIComponent(this.search)}&event=${this.event.id}`
      }

      if (this.next) {
        endpoint = this.next
      }

      this.loading = true
      apiService(endpoint).then((data) => {
        const items = Array.isArray(data) ? data : (data && data.results ? data.results : [])
        this.events = this.events.concat(items)
        this.next = data && data.next ? data.next : null
      }).finally(() => {
        this.loading = false
      })
    },
  },
}
</script>

<style scoped>
.assistance-header-actions {
  align-items: center;
}

.assistance-metrics {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.assistance-toolbar,
.assistance-search {
  display: grid;
  gap: 12px;
}

.assistance-search {
  grid-template-columns: minmax(260px, 1fr) auto;
  align-items: center;
}

.assistance-search__field {
  width: 100%;
}

.assistance-status-filter {
  flex-wrap: wrap;
}

.assistance-table-shell {
  overflow: auto;
}

.assistance-table {
  min-width: 1760px;
  table-layout: auto;
}

.assistance-table th,
.assistance-table td {
  vertical-align: top;
  padding: 14px 18px;
}

.assistance-table th,
.assistance-table td {
  white-space: nowrap;
}

.assistance-table th:nth-child(3),
.assistance-table td:nth-child(3),
.assistance-table th:nth-child(4),
.assistance-table td:nth-child(4),
.assistance-table th:nth-child(10),
.assistance-table td:nth-child(10),
.assistance-table th:nth-child(12),
.assistance-table td:nth-child(12) {
  white-space: normal;
  min-width: 150px;
}

.assistance-table th:nth-child(1),
.assistance-table td:nth-child(1),
.assistance-table th:nth-child(2),
.assistance-table td:nth-child(2),
.assistance-table th:nth-child(7),
.assistance-table td:nth-child(7),
.assistance-table th:nth-child(8),
.assistance-table td:nth-child(8),
.assistance-table th:nth-child(13),
.assistance-table td:nth-child(13),
.assistance-table th:nth-child(21),
.assistance-table td:nth-child(21) {
  min-width: 96px;
}

.assistance-table th:nth-child(11),
.assistance-table td:nth-child(11),
.assistance-table th:nth-child(12),
.assistance-table td:nth-child(12) {
  min-width: 180px;
}

.workspace-table tbody tr:nth-child(even) {
  background: rgba(12, 18, 33, 0.96);
}

.workspace-table tbody tr:nth-child(odd) {
  background: rgba(8, 13, 24, 0.96);
}

.workspace-table tbody tr:hover {
  background: rgba(24, 34, 56, 0.98);
}

.workspace-status-pill.is-off {
  background: rgba(255, 122, 122, 0.16);
  color: #ffd7d7;
}

@media (max-width: 900px) {
  .assistance-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .assistance-search {
    grid-template-columns: 1fr;
  }

  .assistance-table thead {
    display: none;
  }

  .assistance-table,
  .assistance-table tbody,
  .assistance-table tr,
  .assistance-table td {
    display: block;
    width: 100%;
  }

  .assistance-table {
    min-width: 0;
    border: none;
    background: transparent;
  }

  .assistance-table tr {
    margin-bottom: 14px;
    padding: 14px;
    border-radius: 18px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(8, 13, 24, 0.96);
  }

  .assistance-table td {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  }

  .assistance-table td:last-child {
    border-bottom: none;
  }

  .assistance-table td::before {
    content: attr(data-label);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(208, 219, 238, 0.68);
    font-weight: 700;
  }
}
</style>
